import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  Snackbar,
  Alert,
  TablePagination,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import TableToolbar from '../components/TableToolbar';
import { useSidebar } from '../components/Layout';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  FileDownload as ExportIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Employees = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { fullTableMode } = useSidebar();
  // Total number of columns in the table (S.No, First Name, Last Name, etc. - count all TableCell in TableHead)
  const TABLE_COLUMNS = 12;
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({
    plan_id: null,
    worker_count: 0,
    worker_count_limit: 0,
    can_create: true
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination and search state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // Immediate input value
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [addressTimeout, setAddressTimeout] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    permanent_address: '',
    postal_code: '',
    work_rate: '',
    mileage_rate: '',
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    fetchData();
    fetchSubscriptionInfo();
  }, [page, rowsPerPage, searchQuery]);

  // Sync input value with search query when it changes from outside
  useEffect(() => {
    setSearchInputValue(searchQuery);
  }, [searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/employees', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchQuery
        }
      });
      setEmployees(response.data.data);
      setTotalItems(response.data.pagination.total);
    } catch (error) {
      enqueueSnackbar('Error fetching data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      //const response = await axios.get(process.env.REACT_APP_API_URL+'/subscriptions/company');
const token = localStorage.getItem('token');
      
const response = await axios.get(process.env.REACT_APP_API_URL+'/v1/subscriptions/company', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
     // const response = await axios.get('http://localhost:5000/api/subscription/company');
      setSubscriptionData(response.data.data);
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    
    // Update input value immediately so user sees what they type
    setSearchInputValue(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout - delay the actual search
    const timeoutId = setTimeout(() => {
      setSearchQuery(value); // This will trigger useEffect to sync, but value should already match
      setPage(0); // Reset to first page when searching
    }, 500);

    setSearchTimeout(timeoutId);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setSelectedEmployee(employee);
      setFormData({
        first_name: employee.first_name,
        last_name: employee.last_name,
        email: employee.email,
        phone: employee.phone || '',
        password: '',
        permanent_address: employee.permanent_address,
        postal_code: employee.postal_code,
        work_rate: employee.work_rate,
        mileage_rate: employee.mileage_rate,
        latitude: employee.latitude,
        longitude: employee.longitude
      });
    } else {
      setSelectedEmployee(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        permanent_address: '',
        postal_code: '',
        work_rate: '',
        mileage_rate: '',
        latitude: '',
        longitude: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmployee(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'permanent_address') {
      console.log('Address input changed:', value);
      
      // Clear existing timeout
      if (addressTimeout) {
        clearTimeout(addressTimeout);
      }

      // Set new timeout
      const timeoutId = setTimeout(() => {
        if (value.length >= 3) {
          console.log('Triggering address search for:', value);
          setIsLoadingAddress(true);
          fetchAddressSuggestions(value);
        } else {
          console.log('Input too short, clearing suggestions');
          setAddressSuggestions([]);
          setShowSuggestions(false);
        }
      }, 300);

      setAddressTimeout(timeoutId);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchAddressSuggestions = async (input) => {
    console.log('Fetching address suggestions for:', input);
    try {
      console.log('Making API call to /employees/places/autocomplete');
      const response = await api.get('/v1/employees/places/autocomplete', {
        params: { input }
      });
      
      console.log('Address suggestions response:', response.data);
      
      if (response.data.data.predictions) {
        console.log('Setting suggestions:', response.data.data.predictions);
        setAddressSuggestions(response.data.data.predictions);
        setShowSuggestions(true);
      } else {
        console.log('No predictions in response:', response.data.data);
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setAddressSuggestions([]);
      setShowSuggestions(false);
      
      // Check for specific error related to referer restrictions
      if (error.response?.data?.error?.includes('referer restrictions') || 
          error.response?.data?.message?.includes('referer restrictions')) {
        enqueueSnackbar(
          'Address autocomplete is currently unavailable. Please enter the address manually.',
          { 
            variant: 'warning',
            autoHideDuration: 5000
          }
        );
      } else {
        enqueueSnackbar('Error fetching address suggestions. Please enter the address manually.', { 
          variant: 'error',
          autoHideDuration: 5000
        });
      }
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleAddressSelect = async (placeId) => {
    console.log('Selected place ID:', placeId);
    try {
      setIsLoadingAddress(true);
      console.log('Making API call to /employees/places/details');
      const response = await api.get('/v1/employees/places/details', {
        params: { place_id: placeId }
      });
      
      console.log('Place details response:', response.data);
      
      if (response.data.data) {
        console.log('Updating form data with:', {
          address: response.data.data.address,
          postal_code: response.data.data.postal_code,
          latitude: response.data.data.latitude,
          longitude: response.data.data.longitude
        });
        setFormData(prev => ({
          ...prev,
          permanent_address: response.data.data.address,
          postal_code: response.data.data.postal_code,
          latitude: response.data.data.latitude,
          longitude: response.data.data.longitude
        }));
        setSelectedPlaceId(placeId);
        setShowSuggestions(false);
      } else {
        console.log('No data in place details response');
        enqueueSnackbar('Could not fetch address details. Please enter the address manually.', { 
          variant: 'warning',
          autoHideDuration: 5000
        });
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Check for specific error related to referer restrictions
      if (error.response?.data?.error?.includes('referer restrictions') || 
          error.response?.data?.message?.includes('referer restrictions')) {
        enqueueSnackbar(
          'Address details are currently unavailable. Please enter the address manually.',
          { 
            variant: 'warning',
            autoHideDuration: 5000
          }
        );
      } else {
        enqueueSnackbar('Error fetching address details. Please enter the address manually.', { 
          variant: 'error',
          autoHideDuration: 5000
        });
      }
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const validateForm = () => {
    const requiredFields = ['first_name', 'last_name', 'email', 'permanent_address', 'work_rate', 'mileage_rate','postal_code','latitude','longitude'];
    if (!selectedEmployee) {
      requiredFields.push('password');
    }
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        enqueueSnackbar(`Please fill in ${field.replace('_', ' ')}`, { variant: 'error' });
        return false;
      }
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'error' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (selectedEmployee) {
        await api.put(`/v1/employees/${selectedEmployee.id}`, formData);
        enqueueSnackbar('Worker updated successfully', { variant: 'success' });
      } else {
        await api.post('/v1/employees', formData);
        enqueueSnackbar('Worker created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchData();
       fetchSubscriptionInfo();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Error saving worker', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this worker?')) {
      try {
        await api.delete(`/v1/employees/${id}`);
        enqueueSnackbar('Worker deleted successfully', { variant: 'success' });
        fetchData();
        fetchSubscriptionInfo();
      } catch (error) {
        enqueueSnackbar('Error deleting worker', { variant: 'error' });
      }
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get('/v1/employees/export', { 
        params: { search: searchQuery },
        responseType: 'blob' 
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'workers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      enqueueSnackbar('Export successful', { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Error exporting workers', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleViewHistory = (id) => {
    navigate(`/employee-history/${id}`);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (addressTimeout) {
        clearTimeout(addressTimeout);
      }
    };
  }, [addressTimeout]);

  // Add this new function to load Google Maps script
  const loadGoogleMapsScript = () => {
    return new Promise((resolve, reject) => {
      if (window.google) {
        resolve(window.google);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.google.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.addEventListener('load', () => {
        resolve(window.google);
      });
      
      script.addEventListener('error', () => {
        reject(new Error('Google Maps script failed to load'));
      });

      document.head.appendChild(script);
    });
  };

  // Add this useEffect to load the script when component mounts
  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        console.log('Google Maps script loaded successfully');
      })
      .catch((error) => {
        console.error('Error loading Google Maps script:', error);
        enqueueSnackbar('Error loading address autocomplete. Please enter address manually.', {
          variant: 'error',
          autoHideDuration: 5000
        });
      });
  }, []);

  // Add this useEffect for adding the CSS class
  useEffect(() => {
    // Add the CSS class to the container
    const style = document.createElement('style');
    style.textContent = `
      @media (min-width: 767px) {
        .css-k008qs {
          display: block !important;
          
        }
      }
    `;
    document.head.appendChild(style);

    // Cleanup on unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Container 
      maxWidth={false} 
      className="css-k008qs"
      sx={{ 
        py: { xs: 0, sm: 2 }, 
        px: { xs: 0, sm: 3 },
        width: '100%',
        backgroundColor: '#f5f5f5'
      }}
    >
      <TableToolbar title="Workers" />
      
     
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: { xs: 2, sm: 2 }, 
        mx: { xs: 0, sm: 2 },
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search workers..."
              value={searchInputValue}
              onChange={handleSearchChange}
              sx={{ width: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1a237e' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>


 <Grid 
              item 
              xs={12} 
              sm={12} 
              md={8} 
              lg={8} 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
                gap: { xs: 1, sm: 1 },
                mt: { xs: 0, sm: 0 }
              }}
            >
    {subscriptionData.can_create ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                mr: { xs: 0, sm: 1 },
                mb: { xs: 1, sm: 0 },
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1'
                }
              }}
              fullWidth={isMobile}
            >
              Create New
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate('/subscriptions')}
              sx={{ 
                mr: 1,
                backgroundColor: '#f50057',
                '&:hover': {
                  backgroundColor: '#c51162'
                }
              }}
            >
              Upgrade Plan
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <ExportIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{
              backgroundColor: '#1a237e',
              '&:hover': {
                backgroundColor: '#0d47a1'
              }
            }}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>


            </Grid>

        </Grid>
      </Paper>

      <Paper sx={{ 
        width: '100%', 
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        mx: { xs: 0, sm: 2 },
        overflowX: { xs: 'auto', sm: 'hidden' }
      }}>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: '#4e0159',
                  color: '#ffffff'
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 0,
                  paddingRight: 1
                }}>S.No</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>First Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Last Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Email</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Phone</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Home Address</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Postal Code</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Created At</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Hourly Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Mileage Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>History</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 2
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS} align="center" sx={{ py: 8 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        Loading employees...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {employees.map((employee, index) => (
                    <TableRow 
                      key={employee.id}
                      hover
                      sx={{ 
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                      }}
                    >
                  <TableCell sx={{ paddingLeft: 0, paddingRight: 1 }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>{employee.first_name}</TableCell>
                  <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>{employee.last_name}</TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{employee.email}</TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{employee.phone}</TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>
                    <Box sx={{ 
                      maxWidth: 200, 
                      maxHeight: 65, 
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                      },
                    }}>
                      {employee.permanent_address}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{employee.postal_code}</TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{new Date(employee.created_at).toLocaleDateString()}</TableCell>
                  <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>${employee.work_rate}</TableCell>
                  <TableCell sx={{ fontWeight: 500, paddingLeft: 1, paddingRight: 1 }}>${employee.mileage_rate}</TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleViewHistory(employee.id)}
                      sx={{
                        backgroundColor: '#1a237e',
                        '&:hover': {
                          backgroundColor: '#0d47a1'
                        }
                      }}
                    >
                      History
                    </Button>
                  </TableCell>
                  <TableCell sx={{ paddingLeft: 1, paddingRight: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(employee)}
                        sx={{
                          color: '#1a237e',
                          '&:hover': {
                            backgroundColor: 'rgba(26, 35, 126, 0.04)'
                          }
                        }}
                      >
                        <EditIcon sx={{ color: '#4e0159' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(employee.id)}
                        sx={{
                          color: '#d32f2f',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                  ))}
                  {loading && employees.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMNS} align="center" sx={{ py: 4, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary">
                            Updating...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #e0e0e0'
          }}
        />
      </Paper>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 1 },
            m: { xs: 0, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2 }
        }}>
          {selectedEmployee ? 'Edit Worker' : 'Create New Worker '}
        </DialogTitle>
        <DialogContent sx={{ 
          pt: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 }
        }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required={!selectedEmployee}
                helperText={selectedEmployee ? "Leave blank to keep current password" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Home Address"
                  name="permanent_address"
                  value={formData.permanent_address}
                  onChange={handleChange}
                  required
                  multiline
                  rows={2}
                  InputProps={{
                    readOnly: Boolean(selectedEmployee),
                    endAdornment: isLoadingAddress && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      zIndex: 1,
                      width: '100%',
                      maxHeight: 200,
                      overflow: 'auto',
                      mt: 1,
                      boxShadow: 3
                    }}
                  >
                    {addressSuggestions.map((suggestion) => (
                      <Box
                        key={suggestion.place_id}
                        onClick={() => handleAddressSelect(suggestion.place_id)}
                        sx={{
                          p: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        {suggestion.description}
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                required
                InputProps={{
                  readOnly: Boolean(selectedEmployee)
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Hourly Rate"
                name="work_rate"
                type="number"
                value={formData.work_rate}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mileage Rate"
                name="mileage_rate"
                type="number"
                value={formData.mileage_rate}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Latitude"
                name="latitude"
                type="text"
                value={formData.latitude}
                onChange={handleChange}
                required
                InputProps={{
                  readOnly: Boolean(selectedEmployee),
                  startAdornment: <Typography sx={{ mr: 1 }}></Typography>
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="longitude"
                name="longitude"
                type="text"
                value={formData.longitude}
                onChange={handleChange}
                required
                InputProps={{
                  readOnly: Boolean(selectedEmployee),
                  startAdornment: <Typography sx={{ mr: 1 }}></Typography>
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 2 },
          px: { xs: 2, sm: 3 },
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #e0e0e0',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={handleCloseDialog}
            fullWidth={isMobile}
            sx={{
              color: '#4e0159',
              '&:hover': {
                color: '#4e0159',
                opacity: 0.8,
                backgroundColor: 'rgba(78, 1, 89, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            fullWidth={isMobile}
            sx={{
              backgroundColor: '#1a237e',
              '&:hover': {
                backgroundColor: '#0d47a1'
              }
            }}
          >
            {selectedEmployee ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Employees; 